// React
import React from 'react';

// Store
import useSiteStore from '../../../store/siteStore.js';
import useCoachStore from "../../../store/coachStore.js";

// Hooks
import { useAppTheme } from '../../../hooks/useAppTheme.js';

// Utils
import { getWeek } from '../../../utils/index.js';

// Components
import { Avatar, Box, Button, IconButton, InputAdornment, Stack, Tab, Tabs, Menu, MenuItem, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { ClearOutlined, PersonOutline, EditOutlined, CalendarMonth, FormatListBulleted, SendOutlined, ArrowBack, SortOutlined, SupervisorAccountOutlined } from "@mui/icons-material";
import AthleteList from "../../custom/AthleteList.js";
import ParentList from "../../custom/ParentList.js";
import CustomTextField from "../../custom/TextField.js";
import WeeklyScheduleMobile from "../../custom/WeeklyScheduleMobile.js";
import YearCalendar from "../../custom/YearCalendar.js";
import CustomSnackbar from '../../custom/CustomSnackbar.js';

// Create Schedule Components
import CreateWeeklyScheduleSettings from '../../custom/CreateWeeklyScheduleSettings.js';
import CreateWeeklyScheduleAthletes from '../../custom/CreateWeeklyScheduleAthletes.js';
import CreateWeeklyScheduleSessions from '../../custom/CreateWeeklyScheduleSessions.js';

// Style
import { blue, grey, green } from "@mui/material/colors/index.js";
import styles from './coachHome.module.css';
import { darkTheme } from '../../../styles/colorSchemes/index.js';
import { getConsistentAvatarColor } from '../../../utils/index.js';

const CoachHomeMobile = (props) => {
    const { athletes, handleChangeTrainingSession, handleSelectAthlete, linkMessage, parents, selectedAthlete, onRefreshUserLists, prepCreateWeeklySchedule, 
            addTrainingSession, startWeek, onChangeStartWeek, handleToggleAthlete, checkedAthletes, handleSend, repetitions,
            textblocks, setTextblocks, handleSendTextblock, handlePutTextblock } = props;
    
    const { failMessage, openWeeklySchedule, selectedTab, setOpenWeeklySchedule, setSelectedTab, setSnackbarOpenFail, setSnackbarOpenSuccess,
        snackbarOpenFail, snackbarOpenSuccess, successMessage, selectedSortingCriteria, setSelectedSortingCriteria, year, setYear, selectedViewAthletesTab, setSelectedViewAthletesTab, setSelectedCalenderWeek } = useSiteStore();

    const { athleteSearch, openLink, parentSearch, setAthleteSearch, setOpenLink, setParentSearch, setRepetitions } = useCoachStore();

    // Theme logic
    const { isDarkMode } = useAppTheme();

    // Ansichtswechsel State
    const [viewMode, setViewMode] = React.useState('week');

    // Initialize selectedViewAthletesTab to calendarWeek when weekly schedule opens
    React.useEffect(() => {
        if (openWeeklySchedule && viewMode === 'week') {
            setSelectedViewAthletesTab('calendarWeek');
        }
    }, [openWeeklySchedule, viewMode, setSelectedViewAthletesTab]);

    // Track previous view when switching away from createWeeklySchedule
    const [previousView, setPreviousView] = React.useState('calendarYear');
    const [previousViewMode, setPreviousViewMode] = React.useState('week');
    
    React.useEffect(() => {
        if (selectedViewAthletesTab !== 'createWeeklySchedule') {
            setPreviousView(selectedViewAthletesTab);
            setPreviousViewMode(viewMode);
        }
    }, [selectedViewAthletesTab, viewMode]);

    const handleViewChange = (event, newView) => {
        if (newView !== null) {
            setViewMode(newView);
            // Sync global view state with mobile view mode
            if (newView === 'week') {
                setSelectedViewAthletesTab('calendarWeek');
            } else if (newView === 'year') {
                setSelectedViewAthletesTab('calendarYear');
            }
        }
    };

    // Filter-Menü
    const [filterAnchorEl, setFilterAnchorEl] = React.useState(null);
    const filterOpen = Boolean(filterAnchorEl);

    const sortOptions = [
        { value: 'NachnameAuf', label: 'Nachname – aufsteigend' },
        { value: 'NachnameAb', label: 'Nachname – absteigend' },
        { value: 'GeburtsdatumAuf', label: 'Alter – aufsteigend' },
        { value: 'GeburtsdatumAb', label: 'Alter – absteigend' },
        { value: 'AmpelAuf', label: 'Ampel – aufsteigend' },
        { value: 'AmpelAb', label: 'Ampel – absteigend' },
        { value: 'RueckmeldedatumAuf', label: 'Rückmeldung – aufsteigend' },
        { value: 'RueckmeldedatumAb', label: 'Rückmeldung – absteigend' },
    ];

    const handleFilterClick = (event) => { setFilterAnchorEl(event.currentTarget); };
    const handleFilterClose = () => { setFilterAnchorEl(null); };
    const handleFilterSelect = (value) => { setSelectedSortingCriteria(value); setFilterAnchorEl(null); };

    // Handler für Create Settings
    const onChangeRepetitions = (event) => { if (event.target.value < 54) setRepetitions(event.target.value); };
    const onChangeYear = (event) => { setYear(event.target.value); };

    // Helper
    const convertBufferToBase64String = (athlete) => {
        if (!athlete?.profile?.picture?.data) return '';
        return btoa(String.fromCharCode(...new Uint8Array(athlete.profile.picture.data)));
    };

    // Style constants
    const tabSx = {
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '0.85rem',
        minHeight: 36,
        color: isDarkMode ? darkTheme.textSecondary : grey[700],
        px: 2.5,
        py: 0.75,
        minWidth: 90,
        borderRadius: '18px',
        border: `1.5px solid ${isDarkMode ? darkTheme.divider : grey[300]}`,
        backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
        transition: 'all 0.3s ease',
        '&:hover': {
            color: isDarkMode ? darkTheme.accent : blue[600],
            borderColor: isDarkMode ? darkTheme.accent : blue[300],
            backgroundColor: isDarkMode ? 'rgba(255, 152, 0, 0.1)' : blue[50],
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        },
        '&.Mui-selected': {
            color: isDarkMode ? '#000000' : 'white',
            backgroundColor: isDarkMode ? darkTheme.accent : blue[600],
            borderColor: isDarkMode ? darkTheme.accent : blue[600],
            fontWeight: 600,
            boxShadow: isDarkMode ? '0 4px 12px rgba(255, 152, 0, 0.3)' : '0 4px 12px rgba(25, 118, 210, 0.3)',
            '&:hover': {
                backgroundColor: isDarkMode ? darkTheme.accentDark : blue[700],
                borderColor: isDarkMode ? darkTheme.accentDark : blue[700],
                color: isDarkMode ? '#000000' : 'white'
            }
        }
    };

    const iconButtonSx = {
        borderRadius: 2,
        bgcolor: isDarkMode ? darkTheme.cardBg : 'white',
        border: isDarkMode ? `1px solid ${darkTheme.cardBorder}` : '1px solid rgba(0, 0, 0, 0.12)',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
        color: isDarkMode ? darkTheme.textSecondary : undefined,
        '&:hover': {
            bgcolor: isDarkMode ? darkTheme.accent : 'primary.main',
            color: isDarkMode ? '#000000' : 'white',
            borderColor: isDarkMode ? darkTheme.accent : 'primary.main',
            boxShadow: isDarkMode ? '0 2px 8px rgba(255, 152, 0, 0.3)' : '0 2px 8px rgba(25, 118, 210, 0.3)'
        }
    };

    const textFieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            bgcolor: isDarkMode ? darkTheme.cardBg : 'white',
            '& fieldset': {
                borderColor: isDarkMode ? darkTheme.cardBorder : 'rgba(0, 0, 0, 0.23)',
                transition: 'border-color 0.2s ease',
            },
            '&:hover fieldset': {
                borderColor: isDarkMode ? darkTheme.accent : 'rgba(33, 150, 243, 0.6)',
            },
            '&.Mui-focused fieldset': {
                borderColor: isDarkMode ? darkTheme.accent : 'rgba(25, 118, 210, 1)',
                borderWidth: 2,
            }
        },
        '& .MuiInputBase-input': {
            color: isDarkMode ? darkTheme.textPrimary : undefined,
        },
        '& .MuiInputBase-input::placeholder': {
            color: isDarkMode ? darkTheme.textSecondary : undefined,
            opacity: 0.5,
        }
    };

    return (
        <Stack sx={{ justifyContent: 'center', 
            px: '10px', 
            alignItems: 'center', 
            minWidth: '0px', 
            width: '100%', 
            boxSizing: 'border-box', 
            overflowX: 'hidden' 
        }}>
            <CustomSnackbar open={snackbarOpenFail} onClose={() => setSnackbarOpenFail(false)} message={failMessage} severity="error" autoHideDuration={4000} />
            <CustomSnackbar open={snackbarOpenSuccess} onClose={() => setSnackbarOpenSuccess(false)} message={successMessage} severity="success" autoHideDuration={4000} />
            <CustomSnackbar open={openLink} onClose={() => setOpenLink(false)} message={linkMessage} severity="success" showCopyAction={true} />

            {openWeeklySchedule === false && (
                <Box sx={{ display: 'flex', width: '100%', minWidth: '0px', justifyContent: 'center', mt: 1 }}>
                    <Tabs variant="scrollable" scrollButtons="auto" value={selectedTab} onChange={(event, newValue) => setSelectedTab(newValue)}
                        sx={{ justifyContent: 'center', minWidth: '0px', overflow: 'visible', '& .MuiTabs-indicator': { display: 'none' }, '& .MuiTabs-flexContainer': { gap: 0.5 }, '& .MuiTabs-scroller': { overflow: 'visible !important' } }}>
                            <Tab label="Athleten" sx={tabSx} />
                            <Tab label="Eltern" sx={tabSx} />
                    </Tabs>
                </Box>
            )}

            {selectedTab === 0 && (
                <Box sx={{ width: '100%' }}>
                    {openWeeklySchedule === true && (
                        <Box sx={{ width: '100%' }}>
                            
                            {/* Wochenplan Erstellen Formular */}
                            {selectedViewAthletesTab === 'createWeeklySchedule' ? (
                                <Box sx={{ width: '100%' }}>
                                    {/* Header with Back and Save buttons */}
                                    <Box sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between', 
                                        alignItems: 'center',
                                        mb: 2, 
                                        mt: 1,
                                        px: 1,
                                        pb: 1,
                                        borderBottom: isDarkMode ? `1px solid ${darkTheme.divider}` : `1px solid ${grey[300]}`
                                    }}>
                                        <Button 
                                            variant="outlined" 
                                            onClick={() => { 
                                                setSelectedViewAthletesTab(previousView);
                                                if (previousViewMode === 'year') {
                                                    setSelectedCalenderWeek(getWeek(new Date()));
                                                }
                                            }}
                                            startIcon={<ArrowBack />}
                                            sx={{
                                                textTransform: 'none',
                                                fontWeight: 500,
                                                borderColor: isDarkMode ? darkTheme.cardBorder : 'divider',
                                                color: isDarkMode ? darkTheme.textPrimary : 'text.primary',
                                                px: 2,
                                                py: 0.75,
                                                borderRadius: 3,
                                                '&:hover': {
                                                    bgcolor: isDarkMode ? darkTheme.cardHover : grey[100],
                                                    borderColor: isDarkMode ? darkTheme.textSecondary : grey[400]
                                                }
                                            }}
                                        >
                                            Zurück
                                        </Button>

                                        {/* Save Button */}
                                        <Button 
                                            variant="contained" 
                                            startIcon={<SendOutlined />} 
                                            onClick={handleSend} 
                                            sx={{ 
                                                bgcolor: green[600],
                                                color: 'white',
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                px: 1.5,
                                                py: 0.75,
                                                borderRadius: 3,
                                                boxShadow: 2,
                                                fontSize: '0.8rem',
                                                '&:hover': { 
                                                    bgcolor: green[700],
                                                    boxShadow: 4
                                                } 
                                            }}
                                        >
                                            Plan speichern
                                        </Button>
                                    </Box>
                                    
                                    {/* Create Sessions Form */}
                                    <Box sx={{ height: 'calc(100vh - 140px)', overflow: 'auto', px: 1 }}>
                                        <CreateWeeklyScheduleSettings
                                            styles={styles}
                                            repetitions={repetitions}
                                            onChangeRepetitions={onChangeRepetitions}
                                            startWeek={startWeek}
                                            onChangeStartWeek={onChangeStartWeek}
                                            year={year}
                                            onChangeYear={onChangeYear}
                                        />
                                        <CreateWeeklyScheduleAthletes
                                            styles={styles}
                                            athletes={athletes}
                                            handleToggleAthlete={handleToggleAthlete}
                                            checkedAthletes={checkedAthletes}
                                            isNationalCoach={false}
                                        />
                                        <CreateWeeklyScheduleSessions
                                            styles={styles}
                                            handleChangeTrainingSession={handleChangeTrainingSession}
                                            addTrainingSession={addTrainingSession}
                                            startWeek={startWeek}
                                            year={year}
                                            isAthlete={false}
                                            textblocks={textblocks}
                                            setTextblocks={setTextblocks}
                                            handleSendTextblock={handleSendTextblock}
                                            handlePutTextblock={handlePutTextblock}
                                        />
                                    </Box>
                                </Box>
                            ) : (
                                /* Normale Ansicht (Wochenplan/Kalender) */
                                <>
                                    <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center',
                                        width: '100%', 
                                        mb: 2, 
                                        mt: 1,
                                        pb: 1,
                                        px: 1,
                                        borderBottom: isDarkMode ? `1px solid ${darkTheme.divider}` : `1px solid ${grey[300]}`,
                                        position: 'relative',
                                        gap: 2
                                    }}>
                                        <Box sx={{ position: 'absolute', left: 0 }}>
                                            <IconButton onClick={() => setOpenWeeklySchedule(false)} size="small" sx={{ color: isDarkMode ? darkTheme.textPrimary : undefined }}>
                                                <ArrowBack />
                                            </IconButton>
                                        </Box>
                   
                                        <ToggleButtonGroup
                                            value={viewMode}
                                            exclusive
                                            onChange={handleViewChange}
                                            aria-label="ansicht"
                                            sx={{
                                                height: 'auto',
                                                backgroundColor: 'transparent',
                                                borderRadius: '20px',
                                                padding: '0px',
                                                marginLeft: '-20px',
                                                '& .MuiToggleButton-root': {
                                                    border: 'none',
                                                    borderRadius: '20px',
                                                    textTransform: 'none',
                                                    fontWeight: 500,
                                                    fontSize: '0.75rem',
                                                    px: 1.4,
                                                    py: 0.5,
                                                    color: isDarkMode ? darkTheme.textSecondary : grey[700],
                                                    backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                    display: 'flex',
                                                    gap: 0.6,
                                                    alignItems: 'center',
                                                    transition: 'all 0.3s ease',
                                                    '&.Mui-selected': {
                                                        backgroundColor: isDarkMode ? darkTheme.accent : blue[500],
                                                        color: isDarkMode ? '#000000' : 'white',
                                                        boxShadow: isDarkMode ? '0 4px 12px rgba(255, 152, 0, 0.3)' : '0 4px 12px rgba(25, 118, 210, 0.3)',
                                                        '&:hover': { backgroundColor: isDarkMode ? darkTheme.accentDark : blue[600] }
                                                    },
                                                    '&:hover': { 
                                                        backgroundColor: isDarkMode ? darkTheme.cardHover : 'white',
                                                        boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                                                    }
                                                }
                                            }}
                                        >
                                            <ToggleButton value="week">
                                                <FormatListBulleted sx={{ fontSize: '1rem', strokeWidth: 1, stroke: 'currentColor' }} />
                                                Woche
                                            </ToggleButton>
                                            <ToggleButton value="year">
                                                <CalendarMonth sx={{ fontSize: '1rem' }} />
                                                Jahr
                                            </ToggleButton>
                                        </ToggleButtonGroup>

                                        {selectedAthlete && (
                                            <Box sx={{ position: 'absolute', right: 15, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography 
                                                    variant="caption" 
                                                    noWrap 
                                                    align="right"
                                                    sx={{ 
                                                        fontWeight: 400, 
                                                        fontSize: '0.7rem',
                                                        lineHeight: 1.1,
                                                        display: 'block',
                                                        maxWidth: '80px',
                                                        color: isDarkMode ? darkTheme.textPrimary : undefined
                                                    }}
                                                >
                                                    {selectedAthlete.profile.firstName}<br/>
                                                    {selectedAthlete.profile.lastName}
                                                </Typography>
                                                <Avatar
                                                    src={`data:image/png;base64,${convertBufferToBase64String(selectedAthlete)}`}
                                                    sx={{
                                                        width: 30,
                                                        height: 30,
                                                        bgcolor: getConsistentAvatarColor(selectedAthlete),
                                                        fontSize: '0.8rem',
                                                        border: '1px solid rgba(0,0,0,0.1)'
                                                    }}
                                                >
                                                    {!selectedAthlete?.profile?.picture?.data && <PersonOutline sx={{ fontSize: 18 }} />}
                                                </Avatar>
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Create Training Session Button */}
                                    {viewMode === 'week' && (
                                        <Box sx={{ 
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            py: 1,
                                            px: 1
                                        }}>
                                            <Button
                                                variant="contained"
                                                startIcon={<EditOutlined />}
                                                onClick={prepCreateWeeklySchedule}
                                                sx={{
                                                    bgcolor: isDarkMode ? darkTheme.accent : blue[600],
                                                    color: isDarkMode ? '#000000' : 'white',
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    py: 1,
                                                    borderRadius: 3,
                                                    boxShadow: 2,
                                                    width: '95%',
                                                    fontSize: '0.875rem',
                                                    '&:hover': {
                                                        bgcolor: isDarkMode ? darkTheme.accentDark : blue[700],
                                                        boxShadow: 4
                                                    }
                                                }}
                                            >
                                                Trainingseinheit erstellen
                                            </Button>
                                        </Box>
                                    )}

                                    {/* Week View */}
                                    {viewMode === 'week' && (
                                        <WeeklyScheduleMobile
                                            handleChangeTrainingSession={handleChangeTrainingSession}
                                            isEditable={true}
                                            isFeedbackEditable={false}
                                            open={openWeeklySchedule}
                                            close={() => setOpenWeeklySchedule(false)}
                                            selectedAthlete={selectedAthlete}
                                            deleteFilesAfterDownload={false}
                                        />
                                    )}

                                    {/* Year View */}
                                    {viewMode === 'year' && (
                                            <Box sx={{ height: 'calc(100vh - 140px)', overflowY: 'auto', overflowX: 'hidden' }}>
                                            <YearCalendar
                                                selectedAthlete={selectedAthlete}
                                                currentYear={year}
                                                prepCreateWeeklySchedule={prepCreateWeeklySchedule}
                                                onYearChange={setYear}
                                            />
                                        </Box>
                                    )}
                                </>
                            )}
                        </Box>
                    )}

                    {openWeeklySchedule === false && (
                        <Stack spacing={1} width="100%">
                            <Box sx={{ p: 2, borderBottom: isDarkMode ? `1px solid ${darkTheme.divider}` : `1px solid ${grey[300]}`, bgcolor: isDarkMode ? darkTheme.headerBg : 'rgba(0, 0, 0, 0.01)', display: 'flex', gap: 1.5, alignItems: 'center', mb: 1 }}>
                                <CustomTextField fullWidth size="small" variant="outlined" name='text' autoComplete='off' placeholder={`${athletes?.filter(a => a.profile.lastName !== null).length || 0} Athleten durchsuchen...`} value={athleteSearch} onChange={e => setAthleteSearch(e.target.value)}
                                    InputProps={{ 
                                        startAdornment: (<InputAdornment position='start'><PersonOutline sx={{ color: isDarkMode ? darkTheme.textSecondary : 'action.active', fontSize: 20 }} /></InputAdornment>), 
                                        endAdornment: athleteSearch && (<InputAdornment position='end'><IconButton size="small" onClick={() => setAthleteSearch('')} sx={{ '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' } }}><ClearOutlined sx={{ fontSize: 18 }} /></IconButton></InputAdornment>) 
                                    }}
                                    sx={textFieldSx} 
                                />
                                <IconButton onClick={handleFilterClick} sx={iconButtonSx}><SortOutlined /></IconButton>
                                <Menu 
                                    anchorEl={filterAnchorEl} 
                                    open={filterOpen} 
                                    onClose={handleFilterClose}
                                    PaperProps={{
                                        sx: {
                                            borderRadius: 2,
                                            mt: 0.5,
                                            boxShadow: isDarkMode ? '0 4px 20px rgba(0, 0, 0, 0.4)' : '0 4px 20px rgba(0, 0, 0, 0.15)',
                                            minWidth: 280,
                                            bgcolor: isDarkMode ? darkTheme.cardBg : undefined,
                                            border: isDarkMode ? `1px solid ${darkTheme.cardBorder}` : undefined,
                                        }
                                    }}
                                >
                                    {sortOptions.map((opt) => (
                                        <MenuItem 
                                            key={opt.value} 
                                            selected={opt.value === selectedSortingCriteria} 
                                            onClick={() => handleFilterSelect(opt.value)}
                                            sx={{
                                                py: 1,
                                                px: 2,
                                                mx: 1,
                                                my: 0.5,
                                                borderRadius: 2,
                                                transition: 'all 0.2s ease',
                                                color: isDarkMode ? darkTheme.textPrimary : undefined,
                                                '&:hover': {
                                                    bgcolor: isDarkMode ? darkTheme.cardHover : blue[50],
                                                },
                                                '&.Mui-selected': {
                                                    bgcolor: isDarkMode ? `${darkTheme.accent}22` : blue[100],
                                                    color: isDarkMode ? darkTheme.accent : undefined,
                                                    '&:hover': {
                                                        bgcolor: isDarkMode ? `${darkTheme.accent}33` : blue[100],
                                                    }
                                                }
                                            }}
                                        >
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Menu>
                            </Box>
                            <AthleteList openAddUser={false} onOpenAddUser={false} onCloseAddUser={false} isMobile={true} search={athleteSearch} isEditable={false} athletes={athletes} isNationalCoach={false} handleSelectAthlete={handleSelectAthlete} onRefreshUserLists={onRefreshUserLists} />
                        </Stack>
                    )}
                </Box>
            )}

            {selectedTab === 1 && (
                <Box sx={{ width: '100%' }}>
                    {parents && parents.length > 0 ? (
                        <>
                            <Box sx={{ p: 2, borderBottom: isDarkMode ? `1px solid ${darkTheme.divider}` : `1px solid ${grey[300]}`, bgcolor: isDarkMode ? darkTheme.headerBg : 'rgba(0, 0, 0, 0.01)', display: 'flex', gap: 1.5, alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                                <CustomTextField fullWidth size="small" variant="outlined" name='text' autoComplete='off' placeholder={`${parents?.filter(p => p.profile.lastName !== null).length || 0} Eltern durchsuchen...`} value={parentSearch} onChange={e => setParentSearch(e.target.value)} 
                                    InputProps={{ 
                                        startAdornment: (<InputAdornment position='start'><PersonOutline sx={{ color: isDarkMode ? darkTheme.textSecondary : 'action.active', fontSize: 20 }} /></InputAdornment>), 
                                        endAdornment: parentSearch && (<InputAdornment position='end'><IconButton size="small" onClick={() => setParentSearch('')} sx={{ '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' } }}><ClearOutlined sx={{ fontSize: 18 }} /></IconButton></InputAdornment>) 
                                    }} 
                                    sx={textFieldSx} 
                                />
                            </Box>
                            <ParentList sx={{ height: '75vh', overflow: 'auto' }} search={parentSearch} athletes={athletes} isEditable={false} parents={parents} showAthleteAmount={true} onRefreshUserLists={onRefreshUserLists}/>
                        </>
                    ) : (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            py: 6,
                            px: 2,
                            minHeight: '300px'
                        }}>
                            <Box sx={{
                                width: { xs: 60, sm: 70, md: 80 },
                                height: { xs: 60, sm: 70, md: 80 },
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                <SupervisorAccountOutlined sx={{ fontSize: { xs: 48, sm: 54, md: 60 }, color: isDarkMode ? darkTheme.textSecondary : grey[400] }} />
                            </Box>
                            <Typography variant='h6' color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'} fontWeight={600} mb={1} sx={{ fontSize: { xs: '1.1rem', sm: '1.15rem', md: '1.25rem' } }}>
                                Keine Eltern vorhanden
                            </Typography>
                            <Typography variant='body2' color={isDarkMode ? darkTheme.textSecondary : 'text.secondary'} sx={{ textAlign: 'center', maxWidth: { xs: 300, sm: 350, md: 400 }, fontSize: { xs: '0.85rem', sm: '0.875rem' } }}>
                                Es sind derzeit keine Eltern zugeordnet.
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}
        </Stack>
    )
}

export default CoachHomeMobile;